import { morph, MORPH_OPTIONS } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Generic variant re-render wrapper.
 *
 * Wrap any server-rendered block markup that depends on
 * closest.product.selected_or_first_available_variant (e.g. a variant
 * metafield). On variant change, Horizon's variant-picker fetches the
 * updated HTML and dispatches a `productSelect` event; we pull our own
 * fresh element out of that HTML (matched by data-key, usually the block
 * id, so multiple instances can coexist) and morph ourselves. No
 * client-side state.
 *
 * Follows the same recipe as assets/product-specifications.js — the single
 * established route for variant-dependent blocks in this theme:
 * - Listen on document, not the closest section, so the block keeps
 *   working wherever a merchant places it. The event bubbles up from the
 *   variant-picker; the guards in the handler ignore other products.
 * - Morph with the theme's MORPH_OPTIONS (rejects empty whitespace text
 *   nodes) and a data-key getNodeKey so content that appears/disappears
 *   per variant doesn't drift.
 *
 * @example
 * <variant-rerender data-key="{{ block.id }}" data-product-id="{{ closest.product.id }}">…</variant-rerender>
 */
class VariantRerender extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles the product select event by morphing in the markup for the new variant.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        // Combined listing may swap the product; only update if this is our product.
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const key = this.dataset.key;
        if (!key) return;

        const replacement = html.querySelector(`variant-rerender[data-key="${CSS.escape(key)}"]`);
        if (!replacement) return;

        morph(this, replacement, {
          ...MORPH_OPTIONS,
          getNodeKey: (node) => (node instanceof HTMLElement ? node.dataset.key : undefined),
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[variant-rerender] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('variant-rerender')) {
  customElements.define('variant-rerender', VariantRerender);
}
